/**
 * Google OAuth 2.0 Authorization Code + PKCE flow, côté PWA.
 *
 * Étapes :
 *   1. startSignIn()         → redirect vers Google avec PKCE challenge
 *   2. (Google redirect)     → /oauth-callback?code=...&state=...
 *   3. handleCallback()      → POST code+verifier au Worker → reçoit tokens
 *                              → décode id_token → stocke email + tokens IndexedDB
 *   4. getAccessToken()      → renvoie access_token valide, refresh si expiré
 *   5. signOut()             → clear tokens IndexedDB + reset store
 *
 * Phase 3a scope : `openid email profile` (juste l'identité).
 * Phase 4 : ajouter `https://www.googleapis.com/auth/gmail.readonly` etc.
 */

import { generateCodeVerifier, deriveCodeChallenge, generateState } from "./pkce";
import {
  saveTokens,
  loadTokens,
  clearTokens,
  savePending,
  loadPending,
  clearPending,
  type AuthTokens,
} from "./storage";
import { oauthExchange } from "../workerClient";
import { getValidAccessToken } from "./tokenStore";
import { authState } from "./store.svelte";
import { clearBadge } from "../push/badge";
import { clearAllCaches } from "../gmail/inboxCache";
import { clearContacts } from "../gmail/contacts.svelte";

const AUTH_URL = "https://accounts.google.com/o/oauth2/v2/auth";
// openid email profile      : identifier l'utilisateur (Phase 3a)
// gmail.modify              : lire + modifier labels (read/unread/archive)
//                             — superset de readonly. Phase 4a + B + E.
// gmail.send                : envoyer des mails (Phase 5)
// gmail.settings.basic      : récupérer la signature (sendAs.list, Phase 5)
const SCOPE = [
  "openid",
  "email",
  "profile",
  "https://www.googleapis.com/auth/gmail.modify",
  "https://www.googleapis.com/auth/gmail.send",
  "https://www.googleapis.com/auth/gmail.settings.basic",
].join(" ");

function getRedirectUri(): string {
  return `${window.location.origin}/oauth-callback`;
}

/**
 * Démarre le flow OAuth. Génère PKCE + state, sauve en sessionStorage,
 * redirige vers Google. Ne retourne jamais (la page est remplacée).
 */
export async function startSignIn(): Promise<never> {
  const code_verifier = await generateCodeVerifier();
  const code_challenge = await deriveCodeChallenge(code_verifier);
  const state = generateState();
  const redirect_uri = getRedirectUri();

  savePending({ state, code_verifier, redirect_uri, created_at: Date.now() });

  const params = new URLSearchParams({
    client_id: import.meta.env.VITE_GOOGLE_CLIENT_ID,
    response_type: "code",
    scope: SCOPE,
    redirect_uri,
    code_challenge,
    code_challenge_method: "S256",
    state,
    // access_type=offline → Google retourne un refresh_token (sinon access_token only).
    access_type: "offline",
    // prompt=consent → re-affiche l'écran de consent à chaque sign-in. Garantit
    // qu'on récupère un refresh_token frais (Google n'en redonne pas par défaut
    // si l'user a déjà consenti). Léger friction UX, fiabilité augmentée.
    prompt: "consent",
  });

  window.location.assign(`${AUTH_URL}?${params}`);
  // Cette ligne ne s'exécute jamais (page replaced) — typage TS uniquement.
  throw new Error("redirecting to Google");
}

interface IdTokenPayload {
  email?: string;
  email_verified?: boolean;
  name?: string;
  sub?: string;
}

/**
 * Décode (sans vérifier la signature) le payload d'un JWT id_token.
 * Pas besoin de vérifier la signature : on a reçu le token via TLS depuis
 * Google → on fait confiance à la chaîne de transport.
 */
function decodeIdToken(idToken: string): IdTokenPayload {
  const parts = idToken.split(".");
  if (parts.length !== 3) throw new Error("Invalid id_token format");
  const payload = parts[1];
  const padded = payload + "=".repeat((4 - (payload.length % 4)) % 4);
  const base64 = padded.replace(/-/g, "+").replace(/_/g, "/");
  return JSON.parse(atob(base64));
}

/**
 * Traite le retour de Google sur /oauth-callback. Appelé par App.svelte
 * quand window.location.pathname === "/oauth-callback".
 *
 * Lit ?code=&state= → vérifie state vs sessionStorage (CSRF) → POST au Worker
 * → reçoit tokens → stocke en IndexedDB → met à jour authState → clean URL.
 */
export async function handleCallback(): Promise<void> {
  const params = new URLSearchParams(window.location.search);
  const code = params.get("code");
  const state = params.get("state");
  const error = params.get("error");

  if (error) {
    clearPending();
    throw new Error(
      `OAuth refusé par Google : ${error}${
        params.get("error_description") ? ` — ${params.get("error_description")}` : ""
      }`,
    );
  }
  if (!code || !state) {
    throw new Error("Code ou state manquant dans le callback");
  }

  const pending = loadPending();
  if (!pending) {
    throw new Error("Aucun flow OAuth en cours (sessionStorage vide). Relance le sign-in.");
  }
  if (pending.state !== state) {
    clearPending();
    throw new Error("State mismatch — possible CSRF, le sign-in a été abandonné");
  }

  const tokensRaw = await oauthExchange({
    code,
    code_verifier: pending.code_verifier,
    redirect_uri: pending.redirect_uri,
  });

  if (!tokensRaw.access_token || !tokensRaw.refresh_token || !tokensRaw.id_token) {
    throw new Error(`Token exchange incomplet : ${JSON.stringify(tokensRaw).slice(0, 200)}`);
  }

  const idPayload = decodeIdToken(tokensRaw.id_token);
  if (!idPayload.email) {
    throw new Error("id_token ne contient pas d'email — scope manquant ?");
  }

  const tokens: AuthTokens = {
    refresh_token: tokensRaw.refresh_token,
    access_token: tokensRaw.access_token,
    expires_at: Date.now() + (tokensRaw.expires_in - 60) * 1000,
    email: idPayload.email.toLowerCase(),
    id_token: tokensRaw.id_token,
  };

  await saveTokens(tokens);
  authState.signedIn = true;
  authState.email = tokens.email;
  clearPending();

  // Clean URL : enlève ?code=&state= et le path /oauth-callback
  window.history.replaceState({}, document.title, "/");
}

/**
 * Sign-out : clear local tokens + reset store. Ne révoque pas côté Google
 * (v1 simple ; on pourra ajouter /oauth/revoke plus tard si besoin).
 */
export async function signOut(): Promise<void> {
  await clearTokens();
  await clearBadge();
  await clearAllCaches();
  await clearContacts();
  authState.signedIn = false;
  authState.email = null;
}

/**
 * Renvoie un access_token valide, refresh automatiquement si expiré.
 * Renvoie null si pas signé in (ou si refresh fail → user doit re-auth).
 *
 * Wrapper page-side autour de `tokenStore.getValidAccessToken` (pur,
 * partagé avec le service worker). Cette version applique en plus la
 * politique « clear + reset authState si on avait des tokens mais le
 * refresh a fail » — la page peut prompt un re-sign-in, le SW non.
 */
export async function getAccessToken(): Promise<string | null> {
  const had = await loadTokens();
  if (!had) return null;

  const token = await getValidAccessToken();
  if (token) return token;

  // Avait des tokens mais plus rien de valide → refresh permanent fail
  // (révocation, refresh_token expiré, etc.). Force le re-sign-in.
  console.warn("OAuth refresh failed, clearing tokens");
  await clearTokens();
  authState.signedIn = false;
  authState.email = null;
  return null;
}

/**
 * À appeler une fois au boot pour hydrater l'état d'auth depuis IndexedDB.
 * Vérifie aussi que les tokens sont encore valides (refresh si besoin).
 */
export async function loadInitialAuthState(): Promise<void> {
  const tokens = await loadTokens();
  if (!tokens) {
    authState.signedIn = false;
    authState.email = null;
    return;
  }

  // Hydrate immédiatement (avant le refresh éventuel) pour UI snappy
  authState.signedIn = true;
  authState.email = tokens.email;

  // Si access_token expiré → refresh en arrière-plan. Si fail, getAccessToken
  // clear et reset le store.
  if (Date.now() >= tokens.expires_at) {
    await getAccessToken();
  }
}
